import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { supabase, isSupabaseConfigured } from '../lib/supabase'

function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true">
      <path d="M17.64 9.205c0-.639-.057-1.252-.164-1.841H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615Z" fill="#4285F4" />
      <path d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18Z" fill="#34A853" />
      <path d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332Z" fill="#FBBC05" />
      <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58Z" fill="#EA4335" />
    </svg>
  )
}

/**
 * The sign-in link goes out in ONE language, and this is where the app says
 * which one it should be (issue #33).
 *
 * `?lang=` on the redirect URL is the only signal that reaches the
 * send-email hook for an account that already exists: GoTrue applies
 * signInWithOtp's `options.data` only when it creates the user, so metadata
 * on its own would leave every returning seeker on the fallback language.
 * Both are sent — `data` for the brand-new account it gets recorded on,
 * `lang` for everyone. How the hook then chooses: docs/auth-emails.md.
 *
 * The URL stays on this origin, so it keeps matching the auth redirect
 * allow-list exactly as the bare origin did.
 */
function signInRedirect(language: string): string {
  const url = new URL(window.location.origin)
  url.searchParams.set('lang', language)
  return url.toString()
}

export default function Login() {
  const { t, i18n } = useTranslation()
  const [email, setEmail] = useState('')
  const [sent, setSent] = useState(false)
  const [loading, setLoading] = useState(false)
  const [googleLoading, setGoogleLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function handleGoogleSignIn() {
    if (!isSupabaseConfigured || !supabase) {
      setError(t('errors.notConfigured'))
      return
    }
    setGoogleLoading(true)
    setError(null)
    /*
     * Google has to carry the language too (#35). It used to redirect to the
     * bare origin, so a seeker who signed up with Google arrived with no
     * language signal at all and got whatever their browser happened to say.
     * The magic-link path has always sent `?lang=`; this is the same journey
     * and deserves the same treatment.
     */
    const language = (i18n.language || 'en').split('-')[0]
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: { redirectTo: signInRedirect(language) },
    })
    if (error) {
      setError(error.message)
      setGoogleLoading(false)
    }
    // On success Supabase redirects — no need to setGoogleLoading(false)
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!isSupabaseConfigured || !supabase) {
      setError(t('errors.notConfigured'))
      return
    }
    setLoading(true)
    setError(null)
    // Base language only: the catalogue is keyed 'pl', never 'pl-PL'.
    const language = (i18n.language || 'en').split('-')[0]
    const { error } = await supabase.auth.signInWithOtp({
      email,
      options: {
        emailRedirectTo: signInRedirect(language),
        data: { language },
      },
    })
    if (error) {
      const isRateLimit = error.message.toLowerCase().includes('rate limit') || error.status === 429
      setError(
        isRateLimit
          ? t('login.rateLimited')
          : error.message
      )
      setLoading(false)
    } else {
      setSent(true)
    }
  }

  return (
    <div className="min-h-svh bg-[#FAF7F4] flex flex-col items-center justify-center px-6">
      <div className="w-full max-w-sm page-enter">

        {/* Logo mark */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-[#3D1F62] mb-6">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <circle cx="10" cy="10" r="4" fill="white" opacity="0.9" />
              <circle cx="10" cy="10" r="8" stroke="white" strokeWidth="1.5" opacity="0.4" />
            </svg>
          </div>
          <h1 className="font-display text-2xl text-[#1C1128] font-normal leading-snug">
            {t('home.appName')}
          </h1>
        </div>

        {sent ? (
          <div className="text-center">
            <p className="text-[#3D1F62] text-lg font-display mb-3">{t('login.checkInbox')}</p>
            <p className="text-[#6B5F7A] text-sm leading-relaxed">
              {t('login.weSentLinkTo')} <strong>{email}</strong>.<br />
              {t('login.clickToContinue')}
            </p>
          </div>
        ) : (
          <div className="space-y-4">

            {/* Google Sign-In */}
            <button
              onClick={handleGoogleSignIn}
              disabled={googleLoading}
              className="w-full flex items-center justify-center gap-3 py-3.5 rounded-xl border border-[#E4D5EE] bg-white text-[#1C1128] text-sm font-medium hover:bg-[#F4EFF8] active:scale-[0.98] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {googleLoading ? (
                <div className="w-4 h-4 rounded-full border-2 border-[#3D1F62] border-t-transparent animate-spin" />
              ) : (
                <GoogleIcon />
              )}
              {t('login.continueGoogle')}
            </button>

            {/* Divider */}
            <div className="flex items-center gap-3">
              <div className="flex-1 h-px bg-[#E4D5EE]" />
              <span className="text-xs text-[#C4B5D4]">{t('login.or')}</span>
              <div className="flex-1 h-px bg-[#E4D5EE]" />
            </div>

            {/* Email magic link */}
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Purple card wrapper — Login phase accent (#9B72C0) at 4% tint */}
              <div className="rounded-2xl px-5 py-5" style={{
                background: 'rgba(155,114,192,0.04)',
                borderTop: '3px solid #9B72C0',
                borderRight: '1.5px solid #E4D5EE',
                borderBottom: '1.5px solid #E4D5EE',
                borderLeft: '1.5px solid #E4D5EE',
              }}>
                <p className="text-xs font-medium uppercase tracking-widest mb-3" style={{ color: '#9B72C0' }}>
                  {t('login.yourEmail')}
                </p>
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  required
                  autoFocus
                  placeholder={t('login.emailPlaceholder')}
                  // Border is always the phase accent purple — no E4D5EE base
                  className="w-full px-4 py-3 rounded-xl border bg-white text-[#1C1128] placeholder-[#C4B5D4] focus:outline-none focus:ring-2 focus:ring-[#9B72C0]/20 transition-all text-sm"
                  style={{ borderColor: '#9B72C0' }}
                />
              </div>

              {error && (
                <p className="text-rose-500 text-sm">{error}</p>
              )}

              <button
                type="submit"
                disabled={loading || !email}
                className="w-full py-3.5 rounded-xl bg-[#3D1F62] text-white font-medium text-sm tracking-wide hover:bg-[#4A2875] active:scale-[0.98] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? t('login.sending') : t('login.continueWithEmail')}
              </button>

              <p className="text-center text-xs text-[#6B5F7A] pt-1">
                {t('login.noPassword')}
              </p>
            </form>
          </div>
        )}

        <p className="text-center mt-6">
          <a
            href={i18n.language.startsWith('pl') ? 'https://the-inner-awakening.com/pl' : 'https://the-inner-awakening.com'}
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs text-[#9B72C0] hover:underline"
          >
            {t('login.aboutLink')}
          </a>
        </p>
      </div>
    </div>
  )
}
